import { useNavigate, useRouter } from '@tanstack/react-router';
import { toast } from 'sonner';
import { apiGet, operationsClient } from '@/lib/api';
import { threadsApi } from '@/lib/messaging/api';
import { normalizePhoneForTel } from '@/lib/visits/format';
import { normalizeContactPhoneToE164 } from '@/lib/contacts/phone';
import {
  useCallerNumber,
  useRegistrationState,
  useVoipActions,
  type StartCallOptions,
} from '@/stores/voip.store';
import type { Contact } from '@/types/contact';

type ContactLike = Pick<Contact, 'id' | 'primaryEmail' | 'primaryPhone' | 'phoneNumbers'>;
type InboxSearch = { view: 'all'; q: string; threadId: string | null };
type ContactSearchRow = Pick<Contact, 'id' | 'primaryPhone' | 'phoneNumbers'> & {
  altPhone?: string | null;
};

/** Last ten digits — the same key the contacts search matches phones on. */
function phoneKey(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '').slice(-10);
}

/**
 * The contact that owns this number, if any.
 *
 * `/contacts?search=` is a fuzzy `contains` match, so a hit only counts when one
 * of its numbers is the same number — otherwise "555-0100" would text whoever
 * happens to have those digits somewhere in theirs.
 */
async function findContactIdByPhone(phone: string): Promise<string | null> {
  const key = phoneKey(phone);
  if (key.length < 7) return null;
  const raw = await apiGet<
    ContactSearchRow[] | { data?: ContactSearchRow[]; items?: ContactSearchRow[] }
  >(operationsClient, '/contacts', { params: { search: key, limit: 10 } });
  const rows = Array.isArray(raw) ? raw : (raw.data ?? raw.items ?? []);
  const match = rows.find((row) =>
    [row.primaryPhone, row.altPhone, ...(row.phoneNumbers ?? []).map((p) => p.number)].some(
      (number) => phoneKey(number) === key,
    ),
  );
  return match?.id ?? null;
}

/**
 * Centralized routing for the Message / Call icons.
 *  - Message → open (find-or-create) the contact's SMS thread in the Inbox.
 *  - Call → place the call: use the in-app softphone when it's registered,
 *    otherwise hand off to the OS dialer via `tel:` (NOT-344).
 *
 * `callNumber` / `messageNumber` do the same for a bare number (call logs, job
 * contacts) where there is no contact record in hand.
 */
export function useContactComms({ inAppOnly = false }: { inAppOnly?: boolean } = {}) {
  const navigate = useNavigate();
  const router = useRouter();
  const registration = useRegistrationState();
  const callerNumber = useCallerNumber();
  const { startCall } = useVoipActions();

  const goToInbox = async (search: InboxSearch) => {
    const options = { to: '/dashboard/inbox' as const, search };
    await router.preloadRoute(options).catch(() => undefined);
    void navigate(options);
  };

  const messageContact = async (contactId: string, phoneNumberId?: string): Promise<boolean> => {
    try {
      const { threadId } = phoneNumberId
        ? await threadsApi.openForCustomer(contactId, phoneNumberId)
        : await threadsApi.openForCustomer(contactId);
      await goToInbox({ view: 'all', q: '', threadId });
      return true;
    } catch {
      toast.error('Could not open the conversation.');
      return false;
    }
  };

  const messageNumber = async (phone: string) => {
    let contactId: string | null;
    try {
      contactId = await findContactIdByPhone(phone);
    } catch {
      toast.error('Could not open the conversation.');
      return;
    }
    if (contactId) {
      await messageContact(contactId);
      return;
    }
    // Nobody saved under this number yet. An unknown texter still has a thread
    // (under a placeholder the contact search skips), so land on the Inbox
    // filtered to the number instead of dead-ending.
    await goToInbox({ view: 'all', q: phoneKey(phone), threadId: null });
  };

  const callContact = (contact: ContactLike, options?: StartCallOptions) => {
    // Resolve from the same source as `getContactPhone` (which the Call icon uses
    // to enable itself), so an enabled icon always has a number to dial — incl.
    // contacts whose only phone lives in `phoneNumbers`.
    const raw =
      contact.primaryPhone ||
      contact.phoneNumbers?.find((p) => p.isPrimary)?.number ||
      contact.phoneNumbers?.[0]?.number ||
      '';
    callNumber(raw, options);
  };

  const callNumber = (raw: string | null | undefined, options?: StartCallOptions) => {
    // Dial an E.164 number (the softphone path sends straight to Telnyx, which
    // rejects formatted values like "(555) 123-0000").
    const dest = normalizeContactPhoneToE164(raw);
    if (!dest) {
      toast.error('No phone number on file for this contact.');
      return;
    }
    if (registration === 'registered' && callerNumber) {
      // Softphone ready AND we have a caller ID → place the call in-app (CallHUD
      // shows the active call). Without a caller ID Telnyx rejects the leg, so
      // we fall through to the OS dialer below instead of starting a dead call.
      startCall(dest, options);
    } else if (registration === 'registering') {
      // Only gate the brief connecting window: the softphone is about to be ready,
      // so don't hand off to the OS dialer mid-connect — ask the user to retry.
      toast.error('The phone is still connecting. Try again in a moment.');
    } else if (inAppOnly) {
      toast.error('The in-app phone is not ready. Check your calling connection and caller ID.');
    } else {
      // Unregistered / failed / calling not configured → hand off to the OS dialer
      // so the Call icon still places a call.
      window.location.href = `tel:${normalizePhoneForTel(dest)}`;
    }
  };

  return { messageContact, messageNumber, callContact, callNumber };
}
